/**
 * Sterilization log (assistant role test 2026-10-20, fix 7; module id
 * `sterilization`, routes under `/v1/sterilization`). One row per sterilizer
 * cycle: when, which sterilizer, load number, the indicator result (pass or
 * fail), a spore test when one was run, the operator and, optionally, the
 * instrument packs in the load. Office-scoped and audited; entries are never
 * deleted, a wrong one is voided with a reason.
 */
import { z } from 'zod';

const Id = z.uuid();
const Instant = z.string().min(1);

export const STERILIZATION_PERMISSIONS = {
  /** See the log. */
  view: 'task.view',
  /** Record a cycle, void one. */
  record: 'task.create',
  /** Register, edit and retire sterilizers (W15). */
  manage: 'task.create',
} as const;

export const STERILIZATION_ENTITY = 'sterilization_cycle';

export const IndicatorResultSchema = z.enum(['pass', 'fail']);
export type IndicatorResult = z.infer<typeof IndicatorResultSchema>;

export const CycleTypeSchema = z.enum(['wrapped', 'unwrapped', 'prevacuum', 'gravity', 'other']);
export type CycleType = z.infer<typeof CycleTypeSchema>;

export const SterilizationCycleSchema = z.object({
  id: Id,
  locationId: Id,
  cycleAt: Instant,
  /** The sterilizer's name when the cycle was recorded. */
  sterilizer: z.string(),
  /** The registered sterilizer (W15); null only for a cycle that predates the registry. */
  sterilizerId: Id.nullable(),
  loadNumber: z.string(),
  cycleType: CycleTypeSchema.nullable(),
  /** Chemical / physical indicator (the class 5 or 6 integrator, the printout). */
  indicator: IndicatorResultSchema,
  /** Spore (biological) test in this load: pending until read, then pass or fail. */
  sporeTest: z.enum(['none', 'pending', 'pass', 'fail']),
  operatorName: z.string(),
  /** Instrument packs in the load ("Crown prep x2, Hygiene x6"), optional. */
  packs: z.string().nullable(),
  notes: z.string().nullable(),
  recordedByName: z.string(),
  createdAt: Instant,
  voidedAt: Instant.nullable(),
  voidReason: z.string().nullable(),
});
export type SterilizationCycle = z.infer<typeof SterilizationCycleSchema>;

export const CycleListQuerySchema = z.object({
  /** Office-local dates, inclusive. Default: the last 30 days. */
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
});

/**
 * Where a sterilizer stands on spore (biological) testing (W15):
 * `never` no spore test recorded, `failed` the latest read test failed,
 * `overdue` the interval since the last test has passed, `due_soon` due
 * within a day, `ok` otherwise.
 */
export const SporeStatusSchema = z.enum(['ok', 'due_soon', 'overdue', 'never', 'failed']);
export type SporeStatus = z.infer<typeof SporeStatusSchema>;

/** A registered sterilizer (W15): named once, picked on every cycle. */
export const SterilizerSchema = z.object({
  id: Id,
  locationId: Id,
  name: z.string(),
  model: z.string().nullable(),
  serialNumber: z.string().nullable(),
  /** Days between spore tests (7 = weekly, the CDC / ADA baseline). */
  sporeIntervalDays: z.number().int(),
  notes: z.string().nullable(),
  archived: z.boolean(),
  lastCycleAt: Instant.nullable(),
  /** Non-voided cycles in the last 30 days. */
  cycles30d: z.number().int(),
  /** The latest spore test run in this sterilizer (read or still pending). */
  lastSporeAt: Instant.nullable(),
  lastSporeResult: z.enum(['pending', 'pass', 'fail']).nullable(),
  /** Spore tests run but not read yet. */
  sporePending: z.number().int(),
  /** Office-local date the next spore test is due (null: never tested). */
  sporeDueOn: z.iso.date().nullable(),
  sporeStatus: SporeStatusSchema,
  /** The load number suggested for this sterilizer's next cycle today. */
  nextLoad: z.string(),
});
export type Sterilizer = z.infer<typeof SterilizerSchema>;

export const SterilizerListSchema = z.object({
  sterilizers: z.array(SterilizerSchema),
  canManage: z.boolean(),
});
export type SterilizerList = z.infer<typeof SterilizerListSchema>;

export const SterilizerBodySchema = z.object({
  name: z.string().trim().min(1).max(80),
  model: z.string().trim().max(80).nullable().optional(),
  serialNumber: z.string().trim().max(80).nullable().optional(),
  sporeIntervalDays: z.number().int().min(1).max(90).optional(),
  notes: z.string().trim().max(500).nullable().optional(),
});
export type SterilizerBody = z.input<typeof SterilizerBodySchema>;

/** `GET /v1/sterilization/sterilizers/:id/history`: one sterilizer's cycles and spore record. */
export const SterilizerHistorySchema = z.object({
  sterilizer: SterilizerSchema,
  cycles: z.array(SterilizationCycleSchema),
  /** Counts over the cycles returned (voided cycles left out). */
  totals: z.object({
    cycles: z.number().int(),
    indicatorFails: z.number().int(),
    sporeTests: z.number().int(),
    sporePassed: z.number().int(),
    sporeFailed: z.number().int(),
    sporePending: z.number().int(),
  }),
});
export type SterilizerHistory = z.infer<typeof SterilizerHistorySchema>;

export const CycleListSchema = z.object({
  cycles: z.array(SterilizationCycleSchema),
  /** The office's registered sterilizers (W15), active ones only. */
  registry: z.array(SterilizerSchema),
  /** Sterilizer names used at this office, most recent first (for the picker). */
  sterilizers: z.array(z.string()),
  /** The next load number suggested for each sterilizer today. */
  nextLoad: z.record(z.string(), z.string()),
  canRecord: z.boolean(),
});
export type CycleList = z.infer<typeof CycleListSchema>;

export const RecordCycleBodySchema = z.object({
  /** Default: now. */
  cycleAt: z.iso.datetime({ offset: true }).optional(),
  /** The registered sterilizer (W15). */
  sterilizerId: Id.optional(),
  /**
   * Older clients: a sterilizer by name. A name not registered yet registers
   * it. One of `sterilizerId` or `sterilizer` is required.
   */
  sterilizer: z.string().trim().min(1).max(80).optional(),
  loadNumber: z.string().trim().min(1).max(40),
  cycleType: CycleTypeSchema.nullable().optional(),
  indicator: IndicatorResultSchema,
  sporeTest: z.enum(['none', 'pending', 'pass', 'fail']).default('none'),
  /** Default: the signed-in user. */
  operatorName: z.string().trim().min(1).max(120).optional(),
  packs: z.string().trim().max(500).nullable().optional(),
  notes: z.string().trim().max(1000).nullable().optional(),
});
export type RecordCycleBody = z.input<typeof RecordCycleBodySchema>;

export const SporeResultBodySchema = z.object({ result: z.enum(['pass', 'fail']) });

export const VoidCycleBodySchema = z.object({
  reason: z.string().trim().min(3).max(300),
});
